import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { BotAvatar, AccountHealthChip, ManagementChip, StatusChip, EmptyState, LiveChip } from "@/components/shared";
import { useLiveEvents } from "@/hooks/useLiveEvents";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { ArrowLeft, PlusCircle, RefreshCw, MoreHorizontal, Trash2, ShieldCheck, Lock, Loader2, Bot } from "lucide-react";

const Card = ({ children, className = "" }) => <div className={`bg-card border border-border rounded-lg p-5 ${className}`}>{children}</div>;
const Row = ({ k, v }) => <div className="flex items-center justify-between py-2 border-b border-border/50 text-sm"><span className="text-muted-foreground">{k}</span><span className="mono">{v}</span></div>;

function BotsTab({ accountId }) {
  const [bots, setBots] = useState(null);
  const navigate = useNavigate();
  const load = useCallback(
    () => api.get(`/telegram-accounts/${accountId}/bots`).then((r) => setBots(r.data.items)).catch(() => setBots([])),
    [accountId]
  );
  useEffect(() => { load(); }, [load]);
  useLiveEvents(["bot.upserted", "bot.provisioned", "bot.created", "account.synced"], () => load());
  if (!bots) return <Skeleton className="h-40 w-full" />;
  if (!bots.length) return <Card><EmptyState icon={Bot} title="No bots discovered yet" desc="Press Sync to discover bots owned by this account." testId="empty-account-bots" /></Card>;
  return (
    <Card className="p-0 overflow-hidden"><table className="w-full text-sm">
      <thead><tr className="bg-secondary/50 border-b border-border text-xs uppercase text-muted-foreground"><th className="px-4 py-3 text-left">Bot</th><th className="px-4 py-3 text-left">Bot ID</th><th className="px-4 py-3 text-left">Management</th><th className="px-4 py-3 text-left">Health</th></tr></thead>
      <tbody>{bots.map((b) => (
        <tr key={b.id} data-testid={`account-bot-${b.id}`} className="border-b border-border/50 hover:bg-secondary/40 cursor-pointer" onClick={() => navigate(`/bots/${b.id}`)}>
          <td className="px-4 py-2.5"><div className="flex items-center gap-2.5"><BotAvatar name={b.name} username={b.username} size={28} /><div><div className="font-medium">{b.name}</div><div className="text-xs text-muted-foreground mono">@{b.username || "—"}</div></div></div></td>
          <td className="px-4 py-2.5 mono text-xs text-muted-foreground">{b.telegram_bot_id}</td>
          <td className="px-4 py-2.5"><ManagementChip status={b.management_status} /></td>
          <td className="px-4 py-2.5"><StatusChip status={b.token_present ? (b.health?.status || "HEALTHY") : "OFFLINE"} /></td>
        </tr>
      ))}</tbody>
    </table></Card>
  );
}

export default function AccountDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [acc, setAcc] = useState(null);
  const [tab, setTab] = useState("Overview");
  const [delOpen, setDelOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get(`/telegram-accounts/${id}`).then((r) => setAcc(r.data)).catch((e) => { toast.error(apiErr(e)); navigate("/accounts"); }), [id, navigate]);
  useEffect(() => { load(); }, [load]);

  useLiveEvents(["account.updated", "account.synced", "bot.upserted", "bot.provisioned"], () => load());

  const sync = async () => { try { await api.post(`/telegram-accounts/${id}/sync`); toast.success("Sync started", { description: "Progress streams in live." }); } catch (e) { toast.error(apiErr(e)); } };
  const disconnect = async () => { setBusy(true); try { await api.delete(`/telegram-accounts/${id}`); toast.success("Account disconnected"); navigate("/accounts"); } catch (e) { toast.error(apiErr(e)); } setBusy(false); };

  if (!acc) return <div className="space-y-4"><Skeleton className="h-24 w-full" /><Skeleton className="h-72 w-full" /></div>;
  const s = acc.sync_stats || {};

  return (
    <div className="space-y-5">
      <button onClick={() => navigate("/accounts")} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="btn-back-accounts"><ArrowLeft size={16} /> Accounts</button>

      <div className="bg-card border border-border rounded-lg p-5 flex flex-wrap items-center gap-4">
        <BotAvatar name={acc.first_name} username={acc.username} size={56} />
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap"><h1 className="text-xl font-bold truncate">{[acc.first_name, acc.last_name].filter(Boolean).join(" ") || acc.username}</h1><AccountHealthChip status={acc.status} /></div>
          <div className="mono text-sm text-primary">{acc.username ? "@" + acc.username : acc.phone_masked}</div>
        </div>
        <div className="flex-1" />
        <div className="flex items-center gap-4 text-center mr-2">
          {[["Owned", acc.counts.owned], ["Managed", acc.counts.FULL_MANAGED], ["Token", acc.counts.TOKEN_CONNECTED], ["Owner", acc.counts.OWNER_CONTROL]].map(([l, v]) => (
            <div key={l}><div className="text-lg font-bold mono">{v}</div><div className="text-[10px] uppercase text-muted-foreground">{l}</div></div>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <LiveChip />
          <Button size="sm" className="gap-1.5" onClick={() => navigate(`/bots/create?account=${id}`)} data-testid="btn-account-create-bot"><PlusCircle size={15} /> Create Bot</Button>
          <Button size="sm" variant="outline" className="gap-1.5" onClick={sync} data-testid="btn-account-sync"><RefreshCw size={14} /> Sync</Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild><Button variant="outline" size="icon" data-testid="btn-account-more"><MoreHorizontal size={16} /></Button></DropdownMenuTrigger>
            <DropdownMenuContent align="end"><DropdownMenuItem className="text-rose-500" onClick={() => setDelOpen(true)} data-testid="menu-disconnect"><Trash2 size={14} className="mr-2" /> Disconnect account</DropdownMenuItem></DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="account-tabs">
          {["Overview", "Bots", "Session", "Security"].map((t) => <TabsTrigger key={t} value={t} data-testid={`acc-tab-${t.toLowerCase()}`}>{t}</TabsTrigger>)}
        </TabsList>

        <TabsContent value="Overview" className="mt-5">
          <div className="grid lg:grid-cols-2 gap-5">
            <Card><h3 className="font-semibold mb-3">Last synchronization</h3>
              <Row k="Status" v={acc.last_sync_status || "—"} /><Row k="Last synced" v={acc.last_synced_at ? new Date(acc.last_synced_at).toLocaleString() : "—"} />
              <Row k="Discovered" v={s.bots_discovered ?? 0} /><Row k="Added" v={s.bots_added ?? 0} /><Row k="Updated" v={s.bots_updated ?? 0} /><Row k="Removed" v={s.bots_removed ?? 0} />
            </Card>
            <Card><h3 className="font-semibold mb-3">Account</h3>
              <Row k="Telegram user ID" v={acc.telegram_user_id} /><Row k="Username" v={acc.username ? "@" + acc.username : "—"} />
              <Row k="Phone" v={acc.phone_masked} /><Row k="Premium" v={acc.premium ? "Yes" : "No"} /><Row k="Connected" v={acc.last_connected_at ? new Date(acc.last_connected_at).toLocaleString() : "—"} />
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="Bots" className="mt-5"><BotsTab accountId={id} /></TabsContent>

        <TabsContent value="Session" className="mt-5">
          <Card className="max-w-lg space-y-3">
            <div className="flex items-center gap-2"><Lock size={18} className="text-primary" /><h3 className="font-semibold">MTProto session</h3></div>
            <p className="text-sm text-muted-foreground">The session is stored using envelope encryption and is never exposed to the browser.</p>
            <Row k="Session" v="••••••• encrypted" /><Row k="Status" v={acc.status} />
            <Button variant="outline" onClick={() => navigate("/accounts/add")} data-testid="btn-reauth">Reconnect / reauthorize</Button>
          </Card>
        </TabsContent>

        <TabsContent value="Security" className="mt-5">
          <Card className="max-w-lg space-y-3">
            <div className="flex items-center gap-2"><ShieldCheck size={18} className="text-primary" /><h3 className="font-semibold">Security</h3></div>
            <Row k="Session encrypted at rest" v="Yes" /><Row k="OTP / 2FA stored" v="Never" /><Row k="api_hash exposed" v="No" />
            <p className="text-xs text-muted-foreground">OTP and cloud password are ephemeral and discarded immediately after authentication.</p>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={delOpen} onOpenChange={setDelOpen}><DialogContent>
        <DialogHeader><DialogTitle className="text-rose-500">Disconnect account</DialogTitle><DialogDescription>The encrypted session is removed. Discovered bots stay in the workspace but lose their live account link.</DialogDescription></DialogHeader>
        <DialogFooter><Button variant="destructive" onClick={disconnect} disabled={busy} data-testid="btn-confirm-disconnect">{busy ? <Loader2 size={15} className="animate-spin" /> : null} Disconnect</Button></DialogFooter>
      </DialogContent></Dialog>
    </div>
  );
}
